import { ReactNode, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { drivelabels_v2 } from '@googleapis/drivelabels';
import Typography from '@mui/material/Typography';
import { pagesConfig } from '../../../shared/pages-config';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import SharedDialog from '../../../shared/shared-dialog';
import { Messages } from '../../../utils/message-constants';
import DeleteLabelsDialog from './delete-labels-dialog';
import DisableLabelsDialog from './disable-labels-dialog';
import EnableLabelsDialog from './enable-labels-dialog';
import { useDeleteLabels } from './use-delete-labels';
import { usePublishLabel } from './use-publish-label';
import { getLabelLifecycleStatus } from './label-lifecycle';

type Label = drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Label;

export type LabelActionKey = 'publish' | 'delete' | 'disable' | 'enable';

export interface LabelActionHandlers {
  requestPublish: (label: Label) => void;
  isPublishing: boolean;
  /** Opens the Clone page for the label; it needs no confirmation. */
  cloneLabel: (label: Label) => void;
  requestDelete: (labels: Label[]) => void;
  requestDisable: (labels: Label[]) => void;
  requestEnable: (labels: Label[]) => void;
  isDeleting: boolean;
}

export interface LabelActions extends LabelActionHandlers {
  dialogs: ReactNode;
}

interface UseLabelActionsOptions {
  changeLogRoles: string[];
  onChanged?: (labels: Label[], action: LabelActionKey) => void;
}

export function useLabelActions({
  changeLogRoles,
  onChanged,
}: UseLabelActionsOptions): LabelActions {
  const navigate = useNavigate();
  const [labelsToConfirm, setLabelsToConfirm] = useState<Label[]>([]);
  const [labelsToDisable, setLabelsToDisable] = useState<Label[]>([]);
  const [labelsToEnable, setLabelsToEnable] = useState<Label[]>([]);
  const [labelToPublish, setLabelToPublish] = useState<Label | null>(null);

  const labelPublishing = usePublishLabel({
    changeLogRoles,
    onPublished: (published) => {
      setLabelToPublish(null);
      onChanged?.([published], 'publish');
    },
  });
  const { publishLabel, isPending: isPublishing } = labelPublishing;
  const requestPublish = useCallback(
    (label: Label) => {
      if (getLabelLifecycleStatus(label) === 'DRAFT') {
        setLabelToPublish(label);
      } else {
        publishLabel(label);
      }
    },
    [publishLabel]
  );

  const labelDeletion = useDeleteLabels({
    changeLogRoles,
    onDeleted: (deleted) => {
      setLabelsToConfirm([]);
      onChanged?.(deleted, 'delete');
    },
  });
  const { deleteLabels, isPending: isDeleting } = labelDeletion;

  const cloneLabel = useCallback(
    (label: Label) => {
      navigate(
        `/${pagesConfig.cloneDriveLabel.link.replace(
          ':id',
          encodeURIComponent(label.id ?? '')
        )}`
      );
    },
    [navigate]
  );
  const requestDelete = useCallback(
    (labels: Label[]) => setLabelsToConfirm(labels),
    []
  );
  const requestDisable = useCallback(
    (labels: Label[]) => setLabelsToDisable(labels),
    []
  );
  const requestEnable = useCallback(
    (labels: Label[]) => setLabelsToEnable(labels),
    []
  );

  const dialogs = (
    <>
      {labelToPublish && !labelPublishing.changeLogDialogProps.open && (
        <SharedDialog
          open
          handleClose={() => setLabelToPublish(null)}
          title={Messages.DriveLabels.PublishTitle}
          dialogContent={
            <Typography>
              {Messages.DriveLabels.PublishWarningLead}{' '}
              {labelToPublish.properties?.title ? (
                <strong>{labelToPublish.properties.title}</strong>
              ) : (
                'this label'
              )}{' '}
              {Messages.DriveLabels.PublishWarningRest}
            </Typography>
          }
          primaryButton={{
            title: 'Publish',
            action: () => publishLabel(labelToPublish),
            loading: isPublishing,
            disabled: isPublishing,
          }}
        />
      )}
      <ChangeLogDialog
        {...labelPublishing.changeLogDialogProps}
        handleClose={() => {
          labelPublishing.changeLogDialogProps.handleClose();
          setLabelToPublish(null);
        }}
      />
      {labelsToConfirm.length > 0 &&
        !labelDeletion.changeLogDialogProps.open && (
          <DeleteLabelsDialog
            open
            handleClose={() => setLabelsToConfirm([])}
            labels={labelsToConfirm}
            isPending={isDeleting}
            onConfirm={() => deleteLabels(labelsToConfirm)}
          />
        )}
      <ChangeLogDialog
        {...labelDeletion.changeLogDialogProps}
        handleClose={() => {
          labelDeletion.changeLogDialogProps.handleClose();
          setLabelsToConfirm([]);
        }}
      />
      {labelsToEnable.length > 0 && (
        <EnableLabelsDialog
          open
          handleClose={() => setLabelsToEnable([])}
          labels={labelsToEnable}
          changeLogRoles={changeLogRoles}
          onEnabled={() => onChanged?.(labelsToEnable, 'enable')}
        />
      )}
      {labelsToDisable.length > 0 && (
        <DisableLabelsDialog
          open
          handleClose={() => setLabelsToDisable([])}
          labels={labelsToDisable}
          changeLogRoles={changeLogRoles}
          onDisabled={() => onChanged?.(labelsToDisable, 'disable')}
        />
      )}
    </>
  );

  return {
    requestPublish,
    isPublishing,
    cloneLabel,
    requestDelete,
    requestDisable,
    requestEnable,
    isDeleting,
    dialogs,
  };
}
